import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  tenantCloneJobDisposition,
  type TenantCloneJob,
  type TenantCloneJobPhase,
  type TenantCloneJobStatus
} from '@bliks-io/contracts';
import { Badge, Button, Tooltip, TooltipContent, TooltipTrigger } from '@bliks-io/ui';

const PHASE_LABEL_KEYS: Record<TenantCloneJobPhase, string> = {
  copying: 'tenantAdmin.clonePhaseCopying',
  provisioning: 'tenantAdmin.clonePhaseProvisioning',
  importing: 'tenantAdmin.clonePhaseImporting'
};

/**
 * Words for each status, EXHAUSTIVE by type: a sixth status is a build error
 * here rather than a job that silently renders as the wrong thing. WHICH
 * reading a status gets is `tenantCloneJobDisposition`'s call, not this table's
 * — the table only supplies copy.
 */
const STATUS_COPY: Record<TenantCloneJobStatus, { badgeKey: string; tooltipKey?: string }> = {
  running: {
    badgeKey: 'tenantAdmin.cloneRunningBadge',
    tooltipKey: 'tenantAdmin.cloneRunningTooltip'
  },
  interrupted: { badgeKey: 'tenantAdmin.cloneInterruptedBadge' },
  failed: { badgeKey: 'tenantAdmin.cloneFailedBadge' },
  importFailed: {
    badgeKey: 'tenantAdmin.cloneImportFailedBadge',
    tooltipKey: 'tenantAdmin.cloneImportFailed'
  },
  completed: {
    badgeKey: 'tenantAdmin.cloneCompletedBadge',
    tooltipKey: 'tenantAdmin.cloneCompleted'
  }
};

interface CloneJobBadgeProps {
  job: TenantCloneJob;
  onDismiss: () => void;
  isDismissing: boolean;
}

/**
 * Marks a tenant row that has an unfinished clone job, in the row itself.
 *
 * Three distinct readings, which is the whole point of the component, and all
 * three come from the shared disposition rather than from a status list spelled
 * out again here:
 * - `running` — a spinner, phase in the tooltip. Progress, not a problem.
 * - `tenantKept` — a WARNING, never an error: the clone succeeded and the
 *   tenant is active and usable. Reporting that as a failure invites a
 *   re-clone, which mints a duplicate. Carries its own dismiss, because these
 *   jobs are deliberately kept out of the alert above the table and would
 *   otherwise have no button anywhere.
 * - `needsDecision` — an error, so the row can be found from that alert, which
 *   carries the explanation and the actions.
 */
export function CloneJobBadge({ job, onDismiss, isDismissing }: CloneJobBadgeProps) {
  const { t } = useTranslation();
  const copy = STATUS_COPY[job.status];

  switch (tenantCloneJobDisposition(job.status)) {
    case 'running':
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Badge renders a div — tabIndex makes the tooltip keyboard-reachable. */}
            <Badge colorScheme="neutral" tabIndex={0} className="gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t(copy.badgeKey)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {t(copy.tooltipKey ?? copy.badgeKey, { phase: t(PHASE_LABEL_KEYS[job.phase]) })}
          </TooltipContent>
        </Tooltip>
      );

    case 'tenantKept':
      return (
        <span className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge colorScheme="warning" tabIndex={0}>
                {t(copy.badgeKey)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t(copy.tooltipKey ?? copy.badgeKey)}
            </TooltipContent>
          </Tooltip>
          <Button size="small" variant="ghost" onClick={onDismiss} loading={isDismissing}>
            {t('tenantAdmin.cloneDismiss')}
          </Button>
        </span>
      );

    case 'needsDecision':
      return <Badge colorScheme="error">{t(copy.badgeKey)}</Badge>;
  }
}
